import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrdenesPermisos } from '../../hooks/useOrdenesPermisos';
import { ordenesKPIsAPI } from '../../lib/ordenesApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
    BarChart3,
    ArrowLeft,
    TrendingUp,
    TrendingDown,
    Download,
    FileSpreadsheet,
    FileText,
    Users,
    Wrench,
    DollarSign,
    Calendar,
    FileDown,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export default function OrdenesReportesPage() {
    const navigate = useNavigate();
    const { tienePermiso, loading: permisosLoading } = useOrdenesPermisos();
    const [loading, setLoading] = useState(true);
    const [kpis, setKpis] = useState(null);
    const [porTipo, setPorTipo] = useState([]);
    const [porTecnico, setPorTecnico] = useState([]);
    const [tendencia, setTendencia] = useState([]);
    const [periodo, setPeriodo] = useState('6meses');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [kpisRes, tipoRes, tecnicoRes, tendenciaRes] = await Promise.all([
                ordenesKPIsAPI.get(),
                ordenesKPIsAPI.porTipoServicio(),
                ordenesKPIsAPI.porTecnico(),
                ordenesKPIsAPI.tendenciaMensual(),
            ]);
            setKpis(kpisRes.data);
            setPorTipo(tipoRes.data);
            setPorTecnico(tecnicoRes.data);
            setTendencia(tendenciaRes.data);
        } catch (err) {
            console.error('Error loading data:', err);
        } finally {
            setLoading(false);
        }
    };

    const exportToCSV = (data, filename) => {
        if (!data || data.length === 0) {
            toast.error('No hay datos para exportar');
            return;
        }
        
        const headers = Object.keys(data[0]);
        const csvContent = [
            headers.join(','),
            ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        toast.success('Archivo exportado');
    };
    
    const exportToPDF = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/ordenes/reportes/pdf?tipo=resumen`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error('Error al generar PDF');
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `reporte_ordenes_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('PDF descargado');
        } catch (err) {
            toast.error('Error al generar el PDF');
        }
    };

    const getMaxValue = (data, key) => {
        if (!data || data.length === 0) return 0;
        return Math.max(...data.map(d => d[key] || 0));
    };

    if (permisosLoading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <BarChart3 className="h-6 w-6" />
                            Reportes y Estadísticas
                        </h1>
                        <p className="text-muted-foreground">Análisis del módulo de órdenes de servicio</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => navigate('/ordenes/reportes/constructor')} data-testid="custom-report-btn">
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                        Constructor
                    </Button>
                    <Button variant="outline" onClick={exportToPDF} data-testid="export-pdf-btn">
                        <FileDown className="h-4 w-4 mr-2" />
                        PDF
                    </Button>
                    <Select value={periodo} onValueChange={setPeriodo}>
                        <SelectTrigger className="w-[150px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="mes">Este mes</SelectItem>
                            <SelectItem value="3meses">3 meses</SelectItem>
                            <SelectItem value="6meses">6 meses</SelectItem>
                            <SelectItem value="anio">Este año</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {loading ? (
                <div className="grid gap-6">
                    <Skeleton className="h-48" />
                    <Skeleton className="h-64" />
                    <Skeleton className="h-64" />
                </div>
            ) : (
                <>
                    {/* KPIs Principales */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Card className="border-2">
                            <CardContent className="pt-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Total Órdenes</p>
                                        <p className="text-3xl font-bold">{kpis?.total_ordenes || 0}</p>
                                    </div>
                                    <FileText className="h-8 w-8 text-muted-foreground" />
                                </div>
                            </CardContent>
                        </Card>
                        
                        <Card className="border-2">
                            <CardContent className="pt-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Este Mes</p>
                                        <p className="text-3xl font-bold">{kpis?.ordenes_mes || 0}</p>
                                    </div>
                                    <Calendar className="h-8 w-8 text-blue-500" />
                                </div>
                            </CardContent>
                        </Card>
                        
                        <Card className="border-2">
                            <CardContent className="pt-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Por Facturar</p>
                                        <p className="text-3xl font-bold">{kpis?.por_facturar || 0}</p>
                                    </div>
                                    <DollarSign className="h-8 w-8 text-yellow-500" />
                                </div>
                            </CardContent>
                        </Card>
                        
                        <Card className="border-2 bg-green-50">
                            <CardContent className="pt-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Facturado (Mes)</p>
                                        <p className="text-3xl font-bold text-green-700">
                                            ${(kpis?.facturacion_mes || 0).toLocaleString('es-CL')}
                                        </p>
                                    </div>
                                    <TrendingUp className="h-8 w-8 text-green-600" />
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Tendencia Mensual */}
                    <Card className="border-2">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle>Tendencia Mensual</CardTitle>
                                <CardDescription>Evolución de órdenes y facturación</CardDescription>
                            </div>
                            <Button variant="outline" size="sm" onClick={() => exportToCSV(tendencia, 'tendencia_mensual')}>
                                <Download className="h-4 w-4 mr-2" />
                                Exportar
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {tendencia.length === 0 ? (
                                <p className="text-center text-muted-foreground py-8">No hay datos de tendencia</p>
                            ) : (
                                <div className="space-y-4">
                                    {/* Chart simple con barras */}
                                    <div className="flex items-end gap-2 h-48">
                                        {tendencia.map((mes, idx) => {
                                            const maxCreadas = getMaxValue(tendencia, 'creadas');
                                            const height = maxCreadas > 0 ? (mes.creadas / maxCreadas) * 100 : 0;
                                            return (
                                                <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                                                    <div className="w-full flex gap-1 justify-center" style={{ height: '160px', alignItems: 'flex-end' }}>
                                                        <div 
                                                            className="w-5 bg-blue-500 rounded-t transition-all"
                                                            style={{ height: `${height}%`, minHeight: mes.creadas > 0 ? '4px' : '0' }}
                                                            title={`Creadas: ${mes.creadas}`}
                                                        />
                                                        <div 
                                                            className="w-5 bg-green-500 rounded-t transition-all"
                                                            style={{ height: `${maxCreadas > 0 ? (mes.facturadas / maxCreadas) * 100 : 0}%`, minHeight: mes.facturadas > 0 ? '4px' : '0' }}
                                                            title={`Facturadas: ${mes.facturadas}`}
                                                        />
                                                    </div>
                                                    <span className="text-xs text-muted-foreground">{mes.nombre_mes?.substring(0, 3)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="flex justify-center gap-6 text-sm">
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-3 bg-blue-500 rounded" />
                                            <span>Creadas</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-3 bg-green-500 rounded" />
                                            <span>Facturadas</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Por Tipo de Servicio */}
                    <div className="grid md:grid-cols-2 gap-6">
                        <Card className="border-2">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <div>
                                    <CardTitle>Por Tipo de Servicio</CardTitle>
                                    <CardDescription>Distribución de órdenes</CardDescription>
                                </div>
                                <Button variant="outline" size="sm" onClick={() => exportToCSV(porTipo, 'ordenes_por_tipo')}>
                                    <FileSpreadsheet className="h-4 w-4" />
                                </Button>
                            </CardHeader>
                            <CardContent>
                                {porTipo.length === 0 ? (
                                    <p className="text-center text-muted-foreground py-8">No hay datos</p>
                                ) : (
                                    <div className="space-y-3">
                                        {porTipo.map((tipo, idx) => {
                                            const maxTotal = getMaxValue(porTipo, 'total');
                                            const width = maxTotal > 0 ? (tipo.total / maxTotal) * 100 : 0;
                                            return (
                                                <div key={idx} className="space-y-1">
                                                    <div className="flex justify-between text-sm">
                                                        <span className="truncate">{tipo._id?.replace(/_/g, ' ')}</span>
                                                        <span className="font-medium">{tipo.total}</span>
                                                    </div>
                                                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                                                        <div 
                                                            className="h-full bg-blue-500 rounded-full transition-all"
                                                            style={{ width: `${width}%` }}
                                                        />
                                                    </div>
                                                    <div className="flex gap-2 text-xs text-muted-foreground">
                                                        <span>Facturadas: {tipo.facturadas}</span>
                                                        <span>|</span>
                                                        <span>Monto: ${(tipo.monto_total || 0).toLocaleString('es-CL')}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Por Técnico */}
                        <Card className="border-2">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <div>
                                    <CardTitle>Por Técnico</CardTitle>
                                    <CardDescription>Rendimiento del equipo</CardDescription>
                                </div>
                                <Button variant="outline" size="sm" onClick={() => exportToCSV(porTecnico, 'ordenes_por_tecnico')}>
                                    <FileSpreadsheet className="h-4 w-4" />
                                </Button>
                            </CardHeader>
                            <CardContent>
                                {porTecnico.length === 0 ? (
                                    <p className="text-center text-muted-foreground py-8">No hay datos de técnicos</p>
                                ) : (
                                    <div className="space-y-4">
                                        {porTecnico.map((tecnico, idx) => (
                                            <div key={idx} className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                                                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                    <Users className="h-5 w-5 text-blue-600" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="font-medium">{tecnico.tecnico_nombre}</p>
                                                    <div className="flex gap-3 text-sm text-muted-foreground">
                                                        <span>Total: {tecnico.total}</span>
                                                        <span>Completadas: {tecnico.completadas}</span>
                                                        <span className="text-blue-600">En proceso: {tecnico.en_proceso}</span>
                                                    </div>
                                                </div>
                                                <Badge variant="outline" className="ml-auto">
                                                    {tecnico.total > 0 ? Math.round((tecnico.completadas / tecnico.total) * 100) : 0}%
                                                </Badge>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Resumen de Estados */}
                    <Card className="border-2">
                        <CardHeader>
                            <CardTitle>Distribución por Estado</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                                {Object.entries(kpis?.ordenes_por_estado || {}).map(([estado, count]) => (
                                    <div key={estado} className="text-center p-4 bg-muted/50 rounded-lg">
                                        <p className="text-2xl font-bold">{count}</p>
                                        <p className="text-sm text-muted-foreground capitalize">{estado.replace(/_/g, ' ')}</p>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
